import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import OpticksNumberCircle from "@/components/ui/OpticksNumberCircle/OpticksNumberCircle";
import step1_1 from "@/assets/integration-steps/google-slim/step_1_1.png";
import step1_2 from "@/assets/integration-steps/google-slim/step_1_2.png";
import step1_3 from "@/assets/integration-steps/google-slim/step_1_3.png";
import step1_4 from "@/assets/integration-steps/google-slim/step_1_4.png";
import step1_5 from "@/assets/integration-steps/google-slim/step_1_5.png";
import step1_6 from "@/assets/integration-steps/google-slim/step_1_6.png";
import step1_7 from "@/assets/integration-steps/google-slim/step_1_7.png";
import ChannelIntegrationTable from "@/components/channel-integration-table/ChannelIntegrationTable";

interface Props {
  stepLabel?: string;
}

const GoogleSlimIntegrationStep1 = ({ stepLabel }: Props) => {
  return (
    <div className="channels-integration-step google-slim-step-1">
      <div className="step-label-row">
        {stepLabel && <OpticksNumberCircle number={stepLabel} />}
        <h6>Create the exclusion audience in Google Ads</h6>
      </div>
      <div className="step-label-row">
        <p>
          First of all, if you have multiple Google Ads accounts, as best
          practice create the Opticks Audience in your Manager Account (MCC) and
          activate the Audience sharing. In order to make Opticks' audience
          available to all your accounts, your MCC (Manager Account) account
          needs to be configured to share audiences with its child accounts.
          Select "Continuous audience sharing".
        </p>
      </div>
      <p>
        You can learn more{" "}
        <OpticksLink
          href="https://support.google.com/google-ads/answer/6123188?hl=en"
          label="here"
        />
        .
      </p>
      <img
        src={step1_1}
        alt="Screenshot of Google Ads Audiences Integration Step 1.1"
      />

      <p>
        Alternatively, you can create the Opticks Audience directly in each of
        your Google Ads accounts, repeating the steps below for each one.
      </p>

      <p>
        To create the Audience, first go to the Google Ads sidebar menu and
        click on Tools {">"} Shared Library {">"} Audience Manager
      </p>

      <p>
        Then, in the "Your data segments" tab, click on the blue button (+) to
        create a new Remarketing list.
      </p>

      <img
        src={step1_2}
        alt="Screenshot of Google Ads Audiences Integration Step 1.2"
      />

      <p>In the next dropdown, select "Website visitors"</p>

      <img
        src={step1_3}
        alt="Screenshot of Google Ads Audiences Integration Step 1.3"
      />

      <p>Now fill the segment information with the following data:</p>

      <ChannelIntegrationTable
        data={[
          {
            category: "Segment name",
            option: "",
            value: "Opticks Audience (invalid traffic)",
          },
          {
            category: "Customer type",
            option: "",
            value: "Do not fill this option",
            valueOptions: {
              allowCopy: false,
              italics: true,
            },
          },
          {
            category: "Segment members",
            option: "select",
            value: "Visitors of a page with specific tags",
          },
        ]}
      />

      <img
        src={step1_4}
        alt="Screenshot of Google Ads Audiences Integration Step 1.4"
      />

      <p>
        Once segment members are set to "Visitors of a page with specific tags",
        a tag selector will appear. Search for a tag named
        "OpticksExcludeVisitor". If it doesn't exist, which is the most common
        case, click under "New tag" and create it. Name it
        "OpticksExcludeVisitor".
      </p>

      <img
        src={step1_5}
        alt="Screenshot of Google Ads Audiences Integration Step 1.5"
      />

      <p>
        Click the "Save" button and then in the "Get the tag" section, search
        for "Event snippet" and look for the "send_to" line. If the
        "OpticksExcludeVisitor" tag did already exist, hover over it and click
        on the info icon in order to see the "Event snippet".
      </p>

      <p>
        An id with the following format will appear there:
        'AW-12345678/aBc1DeF2gHi3JkL4mNo_'
      </p>

      <p>
        Copy that id. This is the id you need to add to the "Google Ads
        Conversion Id Management" section in the Opticks app. You must not copy
        or add this code snippet anywhere else, just add the id to Opticks and
        it will send the event when appropriate.
      </p>
      <img
        src={step1_6}
        alt="Screenshot of Google Ads Audiences Integration Step 1.6"
      />

      <p>
        Once copied, click on "Close", select the checkbox in the
        "OpticksExcludeVisitor" tag and scroll down to membership duration. Set
        it to 365 days, and then click on "Create segment". Your whole segment
        configuration should look like this:
      </p>

      <img
        src={step1_7}
        alt="Screenshot of Google Ads Audiences Integration Step 1.7"
      />

      <p>
        Once finished, go to your{" "}
        <OpticksLink
          href="https://app.optickssecurity.com/integrations/google-ads/pixel"
          label="Opticks Google Ads Conversion Id Management"
        />{" "}
        and add the id you previously copied.
      </p>

      <p>
        The Opticks Audience will now appear in your Audience manager. Once it
        has reached the minimum required population, you will be able to exclude
        it in your Search and Display campaigns, and create negative conversion
        rules in your Performance Max campaigns.
      </p>
    </div>
  );
};

export default GoogleSlimIntegrationStep1;
